import classNames from "classnames";
import type { FC } from "react";
import { useContext } from "react";
import { BsClipboardCheckFill } from "react-icons/bs";
import { HiUsers } from "react-icons/hi";
import { IoHome } from "react-icons/io5";
import { IoTicket } from "react-icons/io5";
import { BiLogOut } from "react-icons/bi";
import { useSidebarContext } from "../context/SidebarContext";
import { ChatContext, ChatContextInterface } from "./MSRContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import isSmallScreen from "../helpers/is-small-screen";
import Global from "../globals/globals";
import { IoIosChatbubbles } from "react-icons/io";
import endpoint from "../MSRP/endPointManager";
import Wrapper from "@/utils/msrpWrapper";

const CompactSidebar: FC = function () {
    const { setWrapper, setIsAuthenticated,wrapper  } =
        useContext(ChatContext) as ChatContextInterface;
    const { isOpenOnSmallScreens, setOpenOnSmallScreens } = useSidebarContext();
    const isMobile = isSmallScreen();

    const location = useLocation();
    const currentPage = location.pathname;
    const navigate = useNavigate();

    const handleLogout = () => {
        wrapper?.closeConnection();
        sessionStorage.clear();
        localStorage.clear();
        setWrapper(null);
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
        window.location.reload();
    };

    const menuItems = [
        {
            label: "Home",
            icon: IoHome,
            path: "/",
            isActive: currentPage === "/",
            color: "from-blue-500 to-purple-500",
        },
        {
            label: "Chats",
            icon: IoIosChatbubbles,
            path: "/Conversations",
            isActive: currentPage === "/Conversations",
            color: "from-purple-300 to-blue-800",
        },
        {
            label: "Tickets",
            icon: IoTicket,
            path: "/tickets",
            isActive: currentPage === "/tickets",
            color: "from-purple-900 to-purple-500",
        },
        {
            label: "Agents",
            icon: HiUsers,
            path: "/agentList",
            isActive: currentPage === "/agentList",
            color: "from-purple-500 to-pink-500",
        },
        {
            label: "Requests",
            icon: BsClipboardCheckFill,
            path: "/requestList",
            isActive: currentPage === "/requestList",
            color: "from-cyan-500 to-blue-500",
        },
    ];
    const filteredMenuItems = menuItems.filter(item => {
        if (item.label === "Chats" && !isMobile) {
            return false;
        }
        return true;
    });

    if (isMobile && !isOpenOnSmallScreens) {
        return null;
    }

    return (
        <>
            {isMobile && isOpenOnSmallScreens && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-20 transition-all duration-300 ease-out"
                    onClick={() => setOpenOnSmallScreens(false)}
                />
            )}

            <div className={classNames(
                "fixed left-0 top-16 bottom-0 w-20 bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-900",
                "shadow-lg dark:shadow-black/40 border-r border-gray-200 dark:border-gray-700",
                "flex flex-col transition-all duration-300 ease-out backdrop-blur-sm",
                {
                    "z-[100]": isMobile,
                    "z-[50]": !isMobile,
                    "transform translate-x-0 opacity-100": !isMobile || isOpenOnSmallScreens,
                    "transform -translate-x-full opacity-0": isMobile && !isOpenOnSmallScreens,
                }
            )}>

                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

                {/* <div className="p-2 border-b border-gray-200/50 dark:border-gray-700/50 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
                    <div className="text-center">
                         <div className="w-10 h-10 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto shadow-md shadow-blue-500/25">
                            <span className="text-white font-bold text-xs">{Global.username}</span>
                        </div> 
                        <div className="mt-1">
                            <div className="text-[10px] font-bold text-transparent bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text">
                                {Global.username}
                            </div>
                        </div>
                    </div>
                </div> */}

                <nav className="flex-1 py-3 px-1.5  ">
                    <div className="space-y-2">
                        {filteredMenuItems.map((item, index) => {
                            const IconComponent = item.icon;
                            return (
                                <div key={item.path} className="relative group">
                                    <Link
                                        to={item.path}
                                        onClick={() => {
                                            if (isMobile) {
                                                setOpenOnSmallScreens(false);
                                            }
                                        }}
                                        className={classNames(
                                            "relative flex flex-col items-center justify-center p-2 rounded-lg",
                                            "transition-all duration-300 ease-out transform hover:scale-105 active:scale-95",
                                            "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500/50",
                                            "border backdrop-blur-sm",
                                            {
                                                [`bg-gradient-to-br ${item.color} text-white shadow-md`]: item.isActive,
                                                "border-transparent": item.isActive,

                                                "bg-white/80 dark:bg-gray-700/80 text-gray-600 dark:text-gray-400": !item.isActive,
                                                "border-gray-200/50 dark:border-gray-600/50": !item.isActive,
                                                "hover:bg-white dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white": !item.isActive,
                                                "hover:shadow-md hover:border-gray-300 dark:hover:border-gray-500": !item.isActive,
                                            }
                                        )}
                                        title={item.label}
                                        style={{
                                            animationDelay: `${index * 75}ms`,
                                        }}
                                    >
                                        <div className="relative mb-1">
                                            <IconComponent className={classNames(
                                                "w-4 h-4 transition-all duration-300 group-hover:scale-110",
                                                {
                                                    "drop-shadow-sm": item.isActive,
                                                }
                                            )} />

                                            {item.isActive && (
                                                <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-white/90 rounded-full animate-pulse shadow-sm" />
                                            )}
                                        </div>

                                        <span className={classNames(
                                            "text-[9px] font-medium text-center leading-tight tracking-wide",
                                            "transition-all duration-300",
                                            {
                                                "text-white/95 font-semibold drop-shadow-sm": item.isActive,
                                                "group-hover:font-semibold": !item.isActive,
                                            }
                                        )}>
                                            {item.label}
                                        </span>

                                        {item.isActive && (
                                            <div className="absolute inset-0 rounded-lg bg-white/10 pointer-events-none" />
                                        )}
                                    </Link>

                                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900/95 dark:bg-gray-700/95 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg backdrop-blur-sm">
                                        {item.label}
                                        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900/95 dark:bg-gray-700/95 rotate-45" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </nav>

                <div className="p-1.5 border-t border-gray-200/60 dark:border-gray-700/40 bg-gradient-to-t from-gray-100/50 to-transparent dark:from-gray-800/50 backdrop-blur-sm">
                    <div className="relative group">
                        <button
                            onClick={handleLogout}
                            className={classNames(
                                "group w-full flex flex-col items-center justify-center p-2 rounded-lg",
                                "transition-all duration-300 ease-out transform hover:scale-105 active:scale-95",
                                "bg-white/80 dark:bg-gray-700/80 text-red-500 dark:text-red-400",
                                "hover:bg-gradient-to-br hover:from-red-50 hover:to-pink-50",
                                "dark:hover:from-red-900/20 dark:hover:to-pink-900/20",
                                "hover:text-red-600 dark:hover:text-red-300 hover:shadow-md",
                                "border border-red-200/60 dark:border-red-800/40",
                                "hover:border-red-300 dark:hover:border-red-700",
                                "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500/50",
                                "backdrop-blur-sm"
                            )}
                            title="Logout"
                        >
                            <div className="relative mb-1">
                                <BiLogOut className="w-4 h-4 transition-all duration-300 group-hover:scale-110" />
                            </div>
                            <span className="text-[9px] font-medium tracking-wide">Logout</span>
                        </button>


                    </div>
                </div>
                <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-50" />
            </div>
        </>
    );
};

export default CompactSidebar;